import {Checkbox} from "antd";
import TextArea from "antd/lib/input/TextArea";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {inProgress, isNullOrUndefined} from "../../../../common/utils";
import {SpecialEquality} from "../../../../components/condition/model/SimpleCondition";
import {isNullCondition} from "../../../../components/condition/utils/isNullCondition";
import KeyConditionInputs from "../../../../components/KeyConditionInputs";
import {IndividualInclusionMemberToolTip} from "../../../../components/individualInclusionMemberToolTip";
import {MemberInfoListActions} from "../../PreviewKeyCondition/model";
import AsyncUsersTable from "../common/asyncUsersTable";
import {ConditionTypes, useGetCondition} from "../common/hooks/GetCondition";
import {useGetIndividualInclusionMemberDetails} from "../common/hooks/GetIndividualInclusionMemberDetails";
import {useGetIndividualInclusions} from "../common/hooks/GetIndividualInclusions";
import {KeyConditionAction, KeyConditionActions} from "../model";
import "./index.css";
import MemberAction from "./member-actions";
import { useIsIndividualInclusionNamingEnabled } from "../common/hooks/IsIndividualInclusionNamingEnabled";

export const SpecialConditionReasonPlaceholder = "State reason for adding";

function SelectedUserDetailsComponent(
  selectedUser,
  nameSize,
  employeeIdSize,
  isIndividualInclusionMember,
  isIndividualInclusionNamingEnabled
) {
  return (
    <>
      <div className="selected-user-name" style={{ fontSize: nameSize }}>
        {selectedUser.fullName}
        {isIndividualInclusionMember ? (
          <IndividualInclusionMemberToolTip
            title={isIndividualInclusionNamingEnabled ? "Individual Inclusion" : "Special member"}
          />
        ) : null}
      </div>
      <div className="selected-user-employee-id" style={{ fontSize: employeeIdSize }}>
        {selectedUser.employeeId}
      </div>
    </>
  );
}

function SpecialConditionComponent(
  {
    isSpecialConditionChecked,
    isIndividualInclusionMember,
    userId,
    userGroupUniqueName,
  }) {
  const specialCondition = useSelector((rootState) => rootState.keyCondition.specialConditionNode);
  const dispatch = useDispatch();
  const individualInclusion = useGetIndividualInclusionMemberDetails(userGroupUniqueName, userId);
  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled();

  const SpecialConditionHeading = isIndividualInclusionNamingEnabled
    ? "Add as individual inclusion"
    : "Add by Special Condition";

  function toggleSpecialCondition() {
    if (isSpecialConditionChecked === true) {
      dispatch(KeyConditionActions.setIsSpecialConditionChecked(false));
    } else {
      dispatch(KeyConditionActions.setIsSpecialConditionChecked(true));
      if (isNullOrUndefined(specialCondition)) {
        const specialConditionNode = SpecialEquality.createSpecialEqualityNode(
          individualInclusion?.reason,
          userId,
        );
        dispatch(KeyConditionActions.setSpecialConditionNode(specialConditionNode.toJSON(true)));
      }
    }
  }

  return (
    <>
      <div className="special-condition-checkbox">
        <Checkbox
          onChange={toggleSpecialCondition}
          disabled={isIndividualInclusionMember}
          defaultChecked={
            isIndividualInclusionMember || (!isNullOrUndefined(specialCondition) && isSpecialConditionChecked)
          }
        >
          (OR) {SpecialConditionHeading}
        </Checkbox>
      </div>
    </>
  );
}

function SpecialConditionReasonComponent({isSpecialConditionChecked, isIndividualInclusionMember, action}) {
  const dispatch = useDispatch();
  const specialCondition = useSelector((rootState) => rootState.keyCondition.specialConditionNode);
  const [specialConditionReason, setSpecialConditionReason] = useState(specialCondition?.reason);
  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled();

  const SpecialConditionHeading = isIndividualInclusionNamingEnabled
    ? "Add as individual inclusion"
    : "Add by Special Condition";

  function getSpecialConditionReasonComponentHeading() {
    if (action === KeyConditionAction.EDIT) {
      return "Edit special condition fields";
    } else {
      return SpecialConditionHeading;
    }
  }

  return (
    <>
      <div className="key-condition-inputs-heading">
        {getSpecialConditionReasonComponentHeading()}
      </div>
      <div className="key-condition-line-separation"></div>
      <div className="special-condition-reason">
        Reason<span className="individual-inclusion-member-text">*</span>
        <br/>
        <TextArea
          className={specialConditionReason?.length > 0 ? "" : "mandatory-special-reason"}
          value={specialCondition?.reason}
          disabled={
            isSpecialConditionChecked === false ||
            (isIndividualInclusionMember && action !== KeyConditionAction.EDIT)
          }
          style={{width: "400px", marginTop: "12px"}}
          placeholder={SpecialConditionReasonPlaceholder}
          autoSize={{minRows: 4, maxRows: 5}}
          onChange={(event) => {
            dispatch(KeyConditionActions.setSpecialConditionReason(event.target.value));
            setSpecialConditionReason(event.target.value);
          }}
        />
      </div>
    </>
  );
}

export const SelectKeyConditionTableHeading = "People who match the key condition";

function SelectKeyConditionComponent({userGroupUniqueName, inputFieldsHeading}) {
  const dispatch = useDispatch();
  const SelectedUserNameFontSize = "24px";
  const SelectedUserEmployeeIdFontSize = "18px";
  const SelectedUserFontSize = "12px";
  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled();

  const {
    modifiedKeyCondition: editedCondition,
    isSpecialConditionChecked,
    selectedUser,
    editingConditionId,
    action,
  } = useSelector((rootState) => {
    return rootState.keyCondition;
  });

  const {
    value: {data, totalElements},
    progress,
    error,
  } = useSelector(
    (rootState) => rootState.membersMatchingConditionList.membersMatchingConditionList,
  );
  const excludedMembers =
    useSelector((rootState) => rootState.membersMatchingConditionList.excludedMembers);

  const isUserExcluded = (user) => Boolean(excludedMembers.find((excludedMember) => excludedMember.userId === user.key))

  const rowClassNameFn = (user) => isUserExcluded(user) ? "member-excluded" : ""

  const {type: editingConditionNodeType} =
  useGetCondition(userGroupUniqueName, editingConditionId) ?? {};
  const shouldRenderKeyConditionInput = !(
    editingConditionNodeType === ConditionTypes.SpecialCondition &&
    action === KeyConditionAction.EDIT
  );

  const columns = useSelector((rootState) => rootState.membersMatchingConditionList.columns);
  const individualInclusions = useGetIndividualInclusions(userGroupUniqueName);

  function isIndividualInclusionMember() {
    if (isNullOrUndefined(individualInclusions)) {
      return false;
    }
    return individualInclusions?.includes(selectedUser?.key);
  }

  const individualInclusionMember = useGetIndividualInclusionMemberDetails(userGroupUniqueName, selectedUser?.key);

  useEffect(() => {
    if (isIndividualInclusionMember() && action !== KeyConditionAction.EDIT) {
      dispatch(KeyConditionActions.setIsSpecialConditionChecked(true));
      const specialConditionNode = SpecialEquality.createSpecialEqualityNode(
        individualInclusionMember?.reason,
        selectedUser?.key,
      );
      dispatch(KeyConditionActions.setSpecialConditionNode(specialConditionNode.toJSON(true)));
    }
  }, []);

  function usersMatchingGivenCondition(page, size) {
    if (!isNullCondition(editedCondition)) {
      dispatch(
        MemberInfoListActions.getMemberInfoList({
          page: page,
          size: size,
          condition: editedCondition,
        }),
      );
    }
  }

  return (
    <>
      <div className="select-key-condition-container">
        {!isNullOrUndefined(selectedUser)
          ? SelectedUserDetailsComponent(
              selectedUser,
              SelectedUserNameFontSize,
              SelectedUserEmployeeIdFontSize,
              isIndividualInclusionMember()
            )
          : null}
        {shouldRenderKeyConditionInput ? (
          <>
            <div className="key-condition-inputs-heading">
              {inputFieldsHeading}
              <>
                {!isNullOrUndefined(selectedUser) ? (
                  <SpecialConditionComponent
                    isSpecialConditionChecked={isSpecialConditionChecked}
                    isIndividualInclusionMember={isIndividualInclusionMember()}
                    userGroupUniqueName={userGroupUniqueName}
                    userId={selectedUser.key}
                  />
                ) : null}
              </>
            </div>
            <div className="key-condition-line-separation"></div>
            <div className="selected-user-key-conditions">
              {!isNullOrUndefined(selectedUser) ? (
                <div className="selected-user">
                  <span className="selected-user-name-label">Name</span>
                  {SelectedUserDetailsComponent(
                    selectedUser,
                    SelectedUserFontSize,
                    SelectedUserFontSize,
                    isIndividualInclusionMember(),
                    isIndividualInclusionNamingEnabled
                  )}
                </div>
              ) : null}
              <KeyConditionInputs isIndividualInclusionMember={isIndividualInclusionMember()} />
            </div>
            {isSpecialConditionChecked === false ? (
              !isNullCondition(editedCondition) ? (
                <>
                  <div className="key-condition-table-heading">
                    {SelectKeyConditionTableHeading}{" "}
                    <span className="key-condition-table-heading-bold">({totalElements})</span>
                  </div>
                  <AsyncUsersTable
                    error={error}
                    dataSource={data}
                    columns={[
                      ...columns,
                      {
                        title: "Actions",
                        render: (user) => (
                          <MemberAction user={user} userGroupName={userGroupUniqueName} />
                        ),
                      },
                    ]}
                    tableId={"UsersMatchingConditions"}
                    loading={inProgress(progress)}
                    totalElements={totalElements}
                    individualInclusions={individualInclusions}
                    dispatchCallBack={usersMatchingGivenCondition}
                    mainApiRequestData={editedCondition}
                    rowClassName={rowClassNameFn}
                  />
                </>
              ) : null
            ) : null}
          </>
        ) : null}

        {!isNullOrUndefined(selectedUser) ? (
          <SpecialConditionReasonComponent
            isSpecialConditionChecked={isSpecialConditionChecked}
            isIndividualInclusionMember={isIndividualInclusionMember()}
            action={action}
          />
        ) : null}
      </div>
    </>
  );
}

export default SelectKeyConditionComponent;
